import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import { MARK } from '../../../constants';

/**
 * EXCLUIR EXIGE DIGITAR O QUE SE EXCLUI.
 *
 * O caminho era um `window.confirm('Tem certeza que deseja excluir?')`. Aquele
 * balão tem dois problemas, e o segundo é o grave:
 *
 *   1. ele não diz O QUE vai ser excluído. "Tem certeza?" sobre uma lista de
 *      vinte linhas é uma pergunta sem sujeito;
 *   2. ENTER confirma. Quem estava digitando na tela e apertou Enter por reflexo
 *      apagou um serviço com meses de lançamento — e não há de onde voltar.
 *
 * Digitar o código resolve os dois de uma vez: a caixa mostra o código, então
 * quem confirma leu o que está apagando, e nenhum reflexo de teclado produz a
 * sequência certa por acidente.
 *
 * A COMPARAÇÃO É TOLERANTE COM A FORMA E ESTRITA COM A IDENTIDADE. Espaço nas
 * pontas, caixa alta ou baixa e o "#" da frente não mudam quem é o serviço, e
 * recusar por causa deles ensinaria a pessoa a brigar com o campo em vez de ler o
 * código. Um dígito diferente recusa.
 */

/** Tira o que é forma e deixa o que é identidade. */
export const mesmoCodigo = (digitado, esperado) => {
  const limpa = (v) => String(v ?? '').trim().replace(/^#/, '').toUpperCase();
  const a = limpa(digitado);
  const b = limpa(esperado);
  return a.length > 0 && a === b;
};

const ConfirmarExclusaoModal = ({
  show,
  // O que vai ser apagado, em uma linha: "Serviço #2026-0047 · ALICE".
  titulo,
  descricao,
  // O motivo de peso, quando há: parcela paga, etapa lançada. Prop própria e não
  // emendada na descrição porque quebra de linha dentro de um parágrafo o HTML
  // engole — o aviso apareceria colado na frase anterior.
  aviso = null,
  // O texto que a pessoa precisa digitar, e que a tela mostra.
  codigo,
  rotuloDoCodigo = 'o código',
  // Uma lista do que vai embora junto. Existe porque exclusão em cascata é a
  // parte que ninguém imagina: apagar o serviço apaga os embriões dele.
  levaTambem = [],
  onConfirmar,
  onClose,
  loading = false,
}) => {
  const [digitado, setDigitado] = useState('');
  const [erro, setErro] = useState(null);
  const campo = useRef(null);

  // Cada abertura começa limpa: o texto da exclusão anterior no campo seria um
  // clique de distância de apagar a coisa errada.
  useEffect(() => {
    if (!show) return;
    setDigitado('');
    setErro(null);
    // O foco vai para o campo, e não para o botão: o botão é o fim do caminho.
    const t = setTimeout(() => campo.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const tecla = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [show, onClose]);

  if (!show) return null;

  const confirmar = () => {
    if (!mesmoCodigo(digitado, codigo)) {
      // O ERRO É ESPECÍFICO. "Código inválido" manda a pessoa procurar o que
      // errou; dizer o que se esperava e o que chegou fecha a dúvida na hora.
      setErro(digitado.trim()
        ? `Não bate. Você digitou "${digitado.trim()}" e o código é "${codigo}".`
        : `Digite ${rotuloDoCodigo} para confirmar.`);
      return;
    }
    setErro(null);
    onConfirmar?.();
  };

  return (
    <JanelaModal chave="exclusao" titulo={`Excluir ${titulo}`}
      subtitulo="Esta ação não pode ser desfeita." Icone={AlertTriangle}
      onFechar={onClose} largura="sm:max-w-md">
        <div className="flex-1 p-4 space-y-3 overflow-y-auto">
          {descricao && <p className="text-sm text-slate-700">{descricao}</p>}

          {aviso && (
            <p className="text-sm text-slate-800 bg-amber-50 border border-amber-200 rounded p-3">
              {MARK.WARN} {aviso}
            </p>
          )}

          {levaTambem.length > 0 && (
            <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-3">
              <p className="font-medium mb-1">{MARK.WARN} Vai embora junto:</p>
              <ul className="list-disc pl-5 space-y-0.5 text-slate-600">
                {levaTambem.map(x => <li key={x}>{x}</li>)}
              </ul>
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-700 mb-1.5">
              Para confirmar, digite {rotuloDoCodigo}:{' '}
              {/* O código à vista. Exclusão por memória é exclusão por sorte. */}
              <span className="font-mono font-semibold text-slate-900 select-all">{codigo}</span>
            </label>
            <input
              ref={campo}
              type="text"
              value={digitado}
              onChange={(e) => { setDigitado(e.target.value); setErro(null); }}
              // Enter aqui é seguro, ao contrário do balão do navegador: só
              // confirma o que o campo já contém, e o campo começa vazio.
              onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }}
              placeholder={codigo}
              autoComplete="off"
              spellCheck="false"
              className={`w-full px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none
                          focus:ring-2 ${erro
                            ? 'border-rose-400 focus:ring-rose-400'
                            : 'border-slate-300 focus:ring-blue-500'}`}
            />
            {erro && <p className="text-xs text-rose-700 mt-1.5">{MARK.WARN} {erro}</p>}
          </div>
        </div>

        <div className="border-t border-slate-200 p-3 bg-slate-50 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-slate-700 bg-white border border-slate-300
                       rounded-lg hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            onClick={confirmar}
            disabled={loading}
            /* Vermelho, e é a única coisa vermelha da tela: aqui a cor não é
               enfeite, é o aviso de que o botão não volta. */
            className="px-3 py-2 text-sm font-medium text-white bg-rose-600 rounded-lg
                       hover:bg-rose-700 disabled:opacity-50"
          >
            {loading ? 'Excluindo…' : 'Excluir'}
          </button>
        </div>
    </JanelaModal>
  );
};

export default ConfirmarExclusaoModal;
